/**
 * Character sheet view (task 20.3): a full read-only render of the
 * CharacterLedger. Every section renders ONLY when it has data, so a sparse
 * sheet (e.g. an `npc` subset, or a level-1 fighter with no spells) degrades
 * gracefully — no empty "Spells: none" clutter.
 *
 * LAYOUT: the app ships no stylesheet, so the adaptive multi-column layout uses
 * inline CSS Grid with `repeat(auto-fit, minmax(…))` — it flows into several
 * columns on a wide screen and collapses to a single column on a phone with no
 * media queries or build step. Read-only for now (editing is a later task).
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { AbilityKey, CharacterLedger } from "@rufus/shared-types";
import { useServices } from "../AppContext.js";
import { friendlyErrorMessage } from "../api-client.js";

/** The six ability scores in canonical order. */
const ABILITY_ORDER: readonly AbilityKey[] = ["str", "dex", "con", "int", "wis", "cha"];
const ABILITY_LABEL: Record<AbilityKey, string> = {
  str: "STR",
  dex: "DEX",
  con: "CON",
  int: "INT",
  wis: "WIS",
  cha: "CHA",
};

/** Render a number with an explicit sign (e.g. "+2", "-1", "+0"). */
function signed(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

/** D&D ability modifier: floor((score - 10) / 2), rendered signed (e.g. "+2", "-1"). */
function abilityModifier(score: number): string {
  return signed(Math.floor((score - 10) / 2));
}

/** A titled sheet section. Renders nothing when `when` is false, so empty
 * sections never appear. */
function Section({
  title,
  testId,
  when = true,
  children,
}: {
  readonly title: string;
  readonly testId?: string;
  readonly when?: boolean;
  readonly children: React.ReactNode;
}) {
  if (!when) return null;
  return (
    <section
      aria-label={title}
      {...(testId ? { "data-testid": testId } : {})}
      style={{ border: "1px solid #ccc", borderRadius: 8, padding: "0.75rem 1rem" }}
    >
      <h2 style={{ marginTop: 0, fontSize: "1.1em" }}>{title}</h2>
      {children}
    </section>
  );
}

export function CharacterSheetScreen() {
  const { api } = useServices();
  const { characterId = "" } = useParams();
  const [character, setCharacter] = useState<CharacterLedger | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .getCharacter(characterId)
      .then((c) => active && setCharacter(c))
      .catch((e) => active && setError(friendlyErrorMessage(e, "Failed to load.")));
    return () => {
      active = false;
    };
  }, [api, characterId]);

  if (error) return <p role="alert">{error}</p>;
  if (!character) return <p>Loading character…</p>;

  const core = character.core;
  const combat = character.combat;
  const resources = character.resources;
  const spells = character.spells;
  const currency = character.currency;
  const prof = character.proficiencies;
  const progression = character.progression;

  // Only render optional/collection sections when they carry data.
  const spellSlots = resources ? Object.entries(resources.spellSlots) : [];
  const classResources = resources ? Object.entries(resources.classResources) : [];
  const hitDice = resources ? Object.entries(resources.hitDice) : [];
  const hasAnySpell = (spells?.known.length ?? 0) > 0 || (spells?.prepared.length ?? 0) > 0;
  const hasAnyProf =
    prof !== undefined &&
    (prof.skills.length > 0 || prof.saves.length > 0 || prof.weapons.length > 0 || prof.tools.length > 0);
  // Show currency whenever the field is present — even all-zero — so a broke
  // character reads "0 gp" rather than a missing section (the absent/zero
  // ambiguity is exactly what prompted this view's expansion). Hide only when
  // the ledger omits currency entirely.
  const hasCurrency = currency !== undefined;

  return (
    <section aria-label="Character sheet">
      <p>
        <Link to="/characters">← Back to characters</Link>
        {character.currentCampaignId ? (
          <>
            {" · "}
            <Link
              to={`/campaigns/${character.currentCampaignId}/play`}
              data-testid="return-to-session"
            >
              Return to your session
            </Link>
          </>
        ) : null}
      </p>

      <h1 style={{ marginBottom: 0 }}>{character.name}</h1>
      {core ? (
        <p data-testid="identity" style={{ marginTop: "0.25rem", opacity: 0.85 }}>
          Level {core.level} {core.ancestry} {core.class}
          {core.background ? ` · ${core.background}` : ""}
        </p>
      ) : null}

      {/* Adaptive grid: multi-column on desktop, single column on a phone. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1rem",
          alignItems: "start",
          marginTop: "1rem",
        }}
      >
        {/* Combat is always present. */}
        <Section title="Combat" testId="combat">
          <p data-testid="hp" style={{ margin: "0.25rem 0" }}>
            HP: {combat.currentHp} / {combat.maxHp}
            {combat.tempHp ? ` (+${combat.tempHp} temp)` : ""}
          </p>
          <p style={{ margin: "0.25rem 0" }}>AC: {combat.ac}</p>
          <p style={{ margin: "0.25rem 0" }}>
            Initiative: {signed(combat.initiativeMod)} · Speed: {combat.speed} ft
          </p>
        </Section>

        <Section title="Ability Scores" testId="abilities" when={core !== undefined}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {core
              ? ABILITY_ORDER.map((key) => (
                  <li key={key} style={{ margin: "0.2rem 0" }}>
                    {ABILITY_LABEL[key]}: {core.abilityScores[key]} ({abilityModifier(core.abilityScores[key])})
                  </li>
                ))
              : null}
          </ul>
          {core ? <p style={{ margin: "0.4rem 0 0", opacity: 0.8 }}>Proficiency bonus: +{core.proficiencyBonus}</p> : null}
        </Section>

        {hasCurrency && currency ? (
          <Section title="Currency" testId="currency">
            <p style={{ margin: "0.25rem 0" }} data-testid="currency-amounts">
              {currency.gp} gp · {currency.sp} sp · {currency.cp} cp
            </p>
          </Section>
        ) : null}

        <Section title="Proficiencies" testId="proficiencies" when={hasAnyProf}>
          {prof && prof.saves.length > 0 ? <p style={{ margin: "0.2rem 0" }}>Saves: {prof.saves.join(", ")}</p> : null}
          {prof && prof.skills.length > 0 ? <p style={{ margin: "0.2rem 0" }}>Skills: {prof.skills.join(", ")}</p> : null}
          {prof && prof.weapons.length > 0 ? <p style={{ margin: "0.2rem 0" }}>Weapons: {prof.weapons.join(", ")}</p> : null}
          {prof && prof.tools.length > 0 ? <p style={{ margin: "0.2rem 0" }}>Tools: {prof.tools.join(", ")}</p> : null}
        </Section>

        <Section title="Conditions" testId="conditions-section" when={character.conditions.length > 0}>
          <ul data-testid="conditions" style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {character.conditions.map((c, i) => (
              <li key={`${c.name}-${i}`}>
                {c.name}
                {c.source ? ` (from ${c.source})` : ""}
                {c.durationRounds !== null ? ` — ${c.durationRounds} rounds` : ""}
                {c.endCondition ? ` — until ${c.endCondition}` : ""}
              </li>
            ))}
          </ul>
        </Section>

        {character.concentration ? (
          <Section title="Concentration" testId="concentration">
            <p style={{ margin: "0.25rem 0" }}>
              Concentrating on {character.concentration.spellId}
              {character.concentration.targets.length > 0 ? ` (targets: ${character.concentration.targets.join(", ")})` : ""}
              {character.concentration.endsAtRound !== null ? ` — ends round ${character.concentration.endsAtRound}` : ""}
            </p>
          </Section>
        ) : null}

        {character.pendingRollContext ? (
          <Section title="Pending Roll" testId="pending-roll">
            <p style={{ margin: "0.25rem 0" }}>
              {character.pendingRollContext.type}
              {character.pendingRollContext.skill ? ` (${character.pendingRollContext.skill})` : ""} — DC{" "}
              {character.pendingRollContext.dc}
            </p>
          </Section>
        ) : null}

        <Section title="Inventory" testId="inventory-section" when={character.inventory.length > 0}>
          <ul data-testid="inventory" style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {character.inventory.map((item) => (
              <li key={item.itemId}>
                {item.name} ×{item.qty}
                {item.equipped ? " (equipped)" : ""}
                {item.attunement ? " (attuned)" : ""}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Spells" testId="spells" when={hasAnySpell}>
          {spells && spells.prepared.length > 0 ? (
            <p style={{ margin: "0.2rem 0" }}>Prepared: {spells.prepared.join(", ")}</p>
          ) : null}
          {spells && spells.known.length > 0 ? (
            <p style={{ margin: "0.2rem 0" }}>Known: {spells.known.join(", ")}</p>
          ) : null}
        </Section>

        <Section title="Spell Slots" testId="spell-slots" when={spellSlots.length > 0}>
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {spellSlots.map(([level, pool]) => (
              <li key={level}>
                Level {level}: {pool.remaining} / {pool.max}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Resources" testId="resources" when={hitDice.length > 0 || classResources.length > 0}>
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {hitDice.map(([die, pool]) => (
              <li key={die}>
                Hit dice {die}: {pool.remaining} / {pool.max}
              </li>
            ))}
            {classResources.map(([name, pool]) => (
              <li key={name}>
                {name}: {pool.remaining} / {pool.max}
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title="Progression"
          testId="progression"
          when={progression !== undefined}
        >
          {progression ? (
            <>
              <p style={{ margin: "0.2rem 0" }}>
                {progression.mode === "xp" ? `XP: ${progression.xp}` : "Milestone advancement"}
              </p>
              {progression.milestones.length > 0 ? (
                <p style={{ margin: "0.2rem 0" }}>Milestones: {progression.milestones.join(", ")}</p>
              ) : null}
            </>
          ) : null}
        </Section>

        <Section title="Knowledge" testId="knowledge" when={character.knowledge.length > 0}>
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {character.knowledge.map((node) => (
              <li key={node.nodeId}>{node.text}</li>
            ))}
          </ul>
        </Section>
      </div>
    </section>
  );
}
